import { useEffect } from 'react'

import { SessionProvider } from 'next-auth/react'
import { DefaultSeo } from 'next-seo'
import type { AppProps } from 'next/app'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import Script from 'next/script'

import clarity from '@microsoft/clarity'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { BrazeProvider } from '@context/useBrazeIntegration'
import { ToastProvider } from '@context/useToast'
import MainLayout from '@layouts/MainLayout'
import { PrismicPreview } from '@prismicio/next/pages'
import { SWRConfig } from 'swr'

import { GTM_ID } from '@lib/analytics/gtm'
import { CartProvider } from '@context/useCartContext'
import { lang, ONWARD_WIDGET_URL } from '@lib/constants'
import useElevar from '@lib/analytics/elevar'
import { pushUserData, invalidateContext } from '@lib/analytics/elevar-data-layer'
import KlaviyoSessionSync from '@components/global/KlaviyoSessionSync'

import '@styles/global.scss'
import { repositoryName } from '../prismicio'

declare global {
	interface Window {
		zE: any
		initializeOnward: (some: any) => void
		onwardApp: {
			updateCartForCheckout: () => void
			updateCartForCheckoutWithoutInsurance: () => void
			updateCartForCheckoutWithInsurance: () => void
			cartChanged: (some: any) => void
		} | null
		onwardClient: any
	}
}

const ProfileOrAuth = dynamic(() => import('@components/profile/layout/ProfileOrAuth'))


type ExtendedAppProps = {
	pageProps: any
	Component: AppProps['Component'] & { Layout?: any }
}

function MyApp({ Component, pageProps }: ExtendedAppProps) {
	const router = useRouter()

	// Initialize Elevar tracking
	useElevar()

	useEffect(() => {
		// Track initial page view
		pushUserData()

		// Track route changes
		const handleRouteChange = (url: string) => {
			posthog.capture('$pageview')
			pushUserData()
			invalidateContext()
		}

		router.events.on('routeChangeComplete', handleRouteChange)
		return () => {
			router.events.off('routeChangeComplete', handleRouteChange)
		}
	}, [router])

	useEffect(() => {
		if (lang !== 'en-us') return
		const clarityId = process.env.NEXT_PUBLIC_CLARITY_ID
		if (!clarityId) return
		if (typeof window === 'undefined') return
		clarity.init(clarityId)
	}, [])

	const isProfilePage = router.pathname.startsWith('/profile')
	const NestedLayout = Component?.Layout || null

	return (
		<PostHogProvider client={posthog}>
		<>
			<DefaultSeo
				titleTemplate="%s | Birchbox"
				description="Birchbox believes in easy to use products that work – and make you look and feel great. Birchbox's subscription service makes it easy to find those products."
				defaultTitle="Beauty Subscriptions | Birchbox"
				openGraph={{
					type: 'website',
					locale: 'en_US',
					url: 'https://www.birchbox.com/',
					site_name: 'Birchbox',
					title: 'Birchbox: Beauty Subscriptions',
					description: `Birchbox believes in easy to use products that work – and make you look and feel great. Birchbox's subscription service makes it easy to find those products.`,
					images: [
						{
							url: 'https://cdn.raster.app/birchbox/51LU9DtGI8?ixlib=js-3.6.0&height=630&width=1200&compress=false&fm=jpg&q=100&s=9fac8b8273f553de8d76fe6a4413da4f',
							width: 1200,
							height: 630,
							alt: 'Birchbox logo',
							type: 'image/jpeg'
						}
					]
				}}
				twitter={{
					handle: '@birchbox',
					site: '@site',
					cardType: 'summary_large_image'
				}}
			/>

			<SessionProvider session={pageProps?.session} refetchInterval={0}>
				<KlaviyoSessionSync />

				{/* Font Awesome - linked to reyjay@retentionbrands.com */}

				<Script strategy="afterInteractive" src="https://kit.fontawesome.com/755e5d98bb.js" />

				{/* Klaviyo — loads the full runtime (popup rendering, event tracking, identify) */}
				<Script
					strategy="afterInteractive"
					src={`https://static.klaviyo.com/onsite/js/${process.env.NEXT_PUBLIC_KLAVIYO_API_KEY}/klaviyo.js`}
				/>

				{/* Google Tag Manager - Global base code (moved to document/head 20231113 --CM) */}

				{lang === 'en-us' && (
					<>
						{/* Zendesk script */}
						<Script
							id="ze-snippet"
							strategy="lazyOnload"
							src="https://static.zdassets.com/ekr/snippet.js?key=07748884-9a35-49cb-8d69-f1c20899ec59"
							onLoad={() => {
								window.zE(() => {
									window.zE.setHelpCenterSuggestions({ search: 'widget' })
								})
							}}
							onError={() => {
								console.log('Error loading Zendesk')
							}}
						/>
					</>
				)}

				<SWRConfig
					value={{
						// disable revalidateOnFocus and revalidateIfStale for every SWR hook
						// that is called after this
						revalidateOnFocus: false,
						revalidateIfStale: false
					}}
				>
					<BrazeProvider>
						<ToastProvider>
							<CartProvider>
								<MainLayout>
									<PrismicPreview repositoryName={repositoryName}>
										{isProfilePage ? (
											<ProfileOrAuth>
												{NestedLayout ? (
													<NestedLayout>
														<Component {...pageProps} />
													</NestedLayout>
												) : (
													<Component {...pageProps} />
												)}
											</ProfileOrAuth>
										) : (
											<Component {...pageProps} />
										)}
									</PrismicPreview>
								</MainLayout>
							</CartProvider>
						</ToastProvider>
					</BrazeProvider>
				</SWRConfig>
			</SessionProvider>
		</>
		</PostHogProvider>
	)
}

export default MyApp
